export const CommissionSavings = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 text-gray-900">
            Save Massive Commission Costs with FSBO
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Commercial real estate commissions can cost you hundreds of thousands of dollars. Keep that money in your pocket by selling directly.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-2xl font-bold mb-6 text-gray-900">
              The True Cost of Real Estate Commissions
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Most commercial real estate brokers charge 5-6% commission on apartment building sales, split between the listing agent and buyer's agent. On a multi-million dollar property, this represents an enormous expense that comes directly out of your proceeds.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              When you sell for sale by owner directly to a buyer, you eliminate both sides of the commission. The buyer doesn't need an agent because they're an experienced investor, and you don't need a listing agent because you're handling the sale yourself.
            </p>

            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h4 className="font-bold text-gray-900 mb-3">Commission Cost Examples:</h4>
                <div className="space-y-3 text-gray-700">
                  <div className="flex justify-between items-center">
                    <span>$2M Sale @ 6%:</span>
                    <span className="font-bold text-red-600">$120,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>$3M Sale @ 6%:</span>
                    <span className="font-bold text-red-600">$180,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>$5M Sale @ 6%:</span>
                    <span className="font-bold text-red-600">$300,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>$10M Sale @ 6%:</span>
                    <span className="font-bold text-red-600">$600,000</span>
                  </div>
                </div>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg">
                <h4 className="font-bold text-gray-900 mb-2">What You Could Do With Commission Savings:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 flex-shrink-0 mt-1"></i>
                    <span>Invest in your next property with more capital</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 flex-shrink-0 mt-1"></i>
                    <span>Pay off other debts or mortgages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 flex-shrink-0 mt-1"></i>
                    <span>Fund retirement or education expenses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 flex-shrink-0 mt-1"></i>
                    <span>Keep more money after taxes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/77e7e0ca8ad69d4531179b1aa38a9191.jpg"
              alt="Commission savings from for sale by owner apartment building sale"
              className="w-full h-full rounded-lg shadow-xl object-cover"
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 md:p-12 text-white">
          <h3 className="text-2xl font-bold mb-8 text-center">
            Beyond Commission: Other Costs You Avoid
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-camera-line text-2xl text-white"></i>
              </div>
              <h4 className="font-bold mb-3">Marketing Expenses</h4>
              <p className="text-white/90 text-sm mb-3">
                Professional photography, drone footage, virtual tours, brochures, and online advertising can cost $5,000-$15,000+
              </p>
              <div className="text-teal-400 font-bold">Save: $5,000-$15,000</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-paint-brush-line text-2xl text-white"></i>
              </div>
              <h4 className="font-bold mb-3">Staging & Repairs</h4>
              <p className="text-white/90 text-sm mb-3">
                Agents often require cosmetic improvements, landscaping, and staging to make the property "market ready"
              </p>
              <div className="text-teal-400 font-bold">Save: $10,000-$50,000</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <h4 className="font-bold mb-3">Carrying Costs</h4>
              <p className="text-white/90 text-sm mb-3">
                Months of mortgage payments, insurance, taxes, and utilities while the property sits on the market
              </p>
              <div className="text-teal-400 font-bold">Save: $20,000-$100,000+</div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="text-xl text-white/90 mb-2">
              <strong>Total Potential Savings on a $3M Property:</strong>
            </p>
            <p className="text-4xl font-bold text-teal-400">
              $215,000 - $345,000+
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};